// Display conditions ("show only if") + answer piping for forms.
//
// JS mirror of backend/lms_info/form_logic.py. The server re-evaluates
// visibility on submit (hidden fields are never required and their answers are
// dropped), so this only has to agree with it, not enforce anything. Keep the
// two in sync when adding ops.
//
// A field may carry `show_if: { field, op, value }` where `field` is an EARLIER
// field id and op is one of SHOW_IF_OPS. Piping: `{{field:<id>}}` tokens in a
// label/help are replaced with the referenced field's current answer.

import { isDisplayOnlyType, isScaleLikeType, scaleBounds } from "./formFieldHelpers";

export const SHOW_IF_OPS = ["is", "is_not", "gte", "lte", "answered"];

export const SHOW_IF_OP_LABELS = {
  is: "is",
  is_not: "is not",
  gte: "is at least",
  lte: "is at most",
  answered: "is answered",
};

// Which ops make sense for the referenced field's type. Text fields can only
// gate on "answered"; scales get numeric comparisons; everything else equality.
export const opsForType = (type) => {
  if (type === "short_text" || type === "long_text") return ["answered"];
  if (isScaleLikeType(type)) return ["is", "is_not", "gte", "lte", "answered"];
  if (type === "yes_no" || type === "checkbox") return ["is", "is_not"];
  if (type === "single_select" || type === "multi_select") return ["is", "is_not", "answered"];
  return [];
};

// Validate a rule against the fields that PRECEDE the field carrying it.
// Returns null when fine, else a short human reason. Mirrors what the backend
// drops on save (target must be earlier + answerable, op must fit the type) and
// adds a value sanity check the editor can surface (an option that was renamed
// or removed would otherwise hide the follow-up forever).
export const validateShowIf = (rule, earlierFields) => {
  if (!rule) return null;
  const ref = (earlierFields || []).find((f) => f.id === rule.field);
  if (!ref || isDisplayOnlyType(ref.type)) return "missing_target";
  if (!opsForType(ref.type).includes(rule.op)) return "bad_op";
  if (rule.op === "answered") return null;
  const t = ref.type;
  if (t === "yes_no" || t === "checkbox") {
    return typeof rule.value === "boolean" ? null : "bad_value";
  }
  if (t === "single_select" || t === "multi_select") {
    // Labels are stored trimmed on save, so compare trimmed here too (the
    // editor keeps whatever the author typed until it round-trips).
    const target = String(rule.value ?? "").trim();
    return (ref.options || []).some((o) => o.label && String(o.label).trim() === target)
      ? null
      : "bad_value";
  }
  if (isScaleLikeType(t)) {
    const n = Number(rule.value);
    const { min, max } = scaleBounds(ref);
    return Number.isFinite(n) && n >= min && n <= max ? null : "bad_value";
  }
  return null;
};

// A sensible default value for a rule on `target` (used when the target or op
// changes and the old value no longer fits).
export const defaultShowIfValue = (target) => {
  if (!target) return "";
  if (target.type === "yes_no" || target.type === "checkbox") return true;
  if (target.type === "single_select" || target.type === "multi_select") {
    return (target.options || []).find((o) => o.label)?.label || "";
  }
  if (isScaleLikeType(target.type)) return scaleBounds(target).min;
  return "";
};

// Field types that can be referenced by a show_if rule or a piping token.
export const canBeReferenced = (type) => !!type && !isDisplayOnlyType(type);
export const canBePiped = (type) =>
  type === "single_select" || isScaleLikeType(type) || type === "yes_no" || type === "short_text";

const asBool = (v) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "true" || s === "1" || s === "yes") return true;
    if (s === "false" || s === "0" || s === "no") return false;
  }
  if (typeof v === "number") return v !== 0;
  return null;
};

const asNumber = (v) => {
  if (typeof v === "boolean") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const isAnswered = (answer) => {
  if (answer === null || answer === undefined) return false;
  if (typeof answer === "string") return answer.trim() !== "";
  if (Array.isArray(answer)) return answer.length > 0;
  return true;
};

export const evaluateCondition = (op, answer, value, refType) => {
  if (op === "answered") return isAnswered(answer);
  if (!SHOW_IF_OPS.includes(op)) return true; // malformed rule never hides
  if (op === "gte" || op === "lte") {
    const a = asNumber(answer);
    const b = asNumber(value);
    if (a === null || b === null) return false;
    return op === "gte" ? a >= b : a <= b;
  }
  let eq;
  if (refType === "yes_no" || refType === "checkbox") {
    // An unchecked checkbox is stored as absent: absent means false there.
    let a = asBool(answer);
    if (a === null && refType === "checkbox") a = false;
    if (a === null) return false;
    eq = a === asBool(value);
  } else if (refType === "multi_select") {
    const selected = Array.isArray(answer) ? answer : isAnswered(answer) ? [answer] : [];
    if (!selected.length) return false;
    eq = selected.map(String).includes(String(value));
  } else if (isScaleLikeType(refType)) {
    if (!isAnswered(answer)) return false;
    const an = asNumber(answer);
    const bn = asNumber(value);
    eq = an !== null && bn !== null && an === bn;
  } else {
    // single_select (and anything else): labels are opaque strings; never
    // coerce numerically ("1" and "01" are different options).
    if (!isAnswered(answer)) return false;
    eq = String(answer) === String(value);
  }
  return op === "is" ? eq : !eq;
};

export const isFieldVisible = (field, responses, fieldsById) => {
  const rule = field?.show_if;
  if (!rule || typeof rule !== "object") return true;
  const ref = fieldsById[rule.field];
  if (!ref) return true;
  if (!isFieldVisible(ref, responses, fieldsById)) return false;
  return evaluateCondition(rule.op, (responses || {})[rule.field], rule.value, ref.type);
};

export const buildFieldsById = (fields) => {
  const out = {};
  (fields || []).forEach((f) => {
    if (f && f.id) out[f.id] = f;
  });
  return out;
};

export const visibleFields = (fields, responses) => {
  const byId = buildFieldsById(fields);
  return (fields || []).filter((f) => isFieldVisible(f, responses, byId));
};

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Piping

const PIPE_RE = /\{\{\s*field:([^}\s]+)\s*\}\}/g;

export const PIPING_PLACEHOLDER = "…";

// Human-readable answer for a piping token.
export const formatAnswer = (answer, refType) => {
  if (!isAnswered(answer)) return null;
  if (refType === "yes_no" || refType === "checkbox") {
    const b = asBool(answer);
    return b === null ? null : b ? "Yes" : "No";
  }
  if (Array.isArray(answer)) return answer.join(", ");
  return String(answer);
};

// Replace {{field:id}} tokens with the respondent's answer (or a placeholder).
// `fallback` controls what an unanswered/unknown reference renders as:
// PIPING_PLACEHOLDER for players, "[answer]" for reports/read-only views, or a
// function (id) => string for the editor (shows the referenced label).
export const resolvePiping = (text, responses, fieldsById, fallback = PIPING_PLACEHOLDER) => {
  if (!text || typeof text !== "string" || text.indexOf("{{") === -1) return text || "";
  return text.replace(PIPE_RE, (_m, id) => {
    const ref = fieldsById?.[id];
    // A source that is currently hidden by its own gate counts as unanswered
    // (its stale answer is dropped on submit, so don't show it either).
    const sourceVisible = ref ? isFieldVisible(ref, responses, fieldsById) : false;
    const shown = sourceVisible ? formatAnswer((responses || {})[id], ref.type) : null;
    if (shown !== null && shown !== undefined) return shown;
    return typeof fallback === "function" ? fallback(id, ref) : fallback;
  });
};

// The token the editor inserts for a piped answer.
export const pipingToken = (id) => `{{field:${id}}}`;

// Split a string into literal chunks ({text}) and piping references ({id}), in
// order, for surfaces that render the token differently from the text around it
// (the editor shows the source question's name in its place). A fresh regex per
// call — PIPE_RE is global, so sharing it would carry lastIndex between calls.
export const splitPiping = (text) => {
  const out = [];
  if (typeof text !== "string" || !text) return out;
  const re = new RegExp(PIPE_RE.source, "g");
  let last = 0;
  let m = re.exec(text);
  while (m !== null) {
    if (m.index > last) out.push({ text: text.slice(last, m.index) });
    out.push({ id: m[1] });
    last = m.index + m[0].length;
    m = re.exec(text);
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
};

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Authoring form: {{answer: <question>}}
//
// Answers are STORED as {{field:<id>}} — ids survive a rename, labels don't,
// and two questions can share wording. But an id says nothing to the author, so
// the editor shows and edits the same token named after the question it pulls
// from. The two forms convert on the way in and out of the input, so what an
// author reads is what they edit, with no second rendering to keep in sync.

// Lazy up to the closing "}}" rather than "anything but a brace": a question
// worded "What does {x} mean?" produced a token that stopped matching at its
// own brace, so it never converted back to an id and reached respondents as
// literal {{answer: …}} text. Deliberately not matching across a newline — a
// token is always on one line, and letting it span lines would swallow text up
// to some unrelated "}}" far below.
const ANSWER_RE = /\{\{\s*answer:\s*(.*?)\}\}/g;

// A question's own wording, as it reads inside a display token. Unclipped, so
// the round trip back to an id is exact; a source that pipes an answer of its
// own collapses that token to "…" rather than nesting one token inside another.
export const pipingSourceName = (field) => {
  if (!field) return "answer";
  const text = resolvePiping(field.label || "", {}, {}, "…").trim();
  return text || "untitled question";
};

// id -> the name its token displays, made UNIQUE across the questions that come
// before this one.
//
// Two questions may legitimately share wording ("What makes you feel that
// way?"), and a name that maps to more than one id can't be converted back:
// picking the second from the menu, or so much as typing a character in a label
// that referenced it, would rebind the token to the first and quietly change
// which answer the respondent sees. Numbering the repeats keeps the mapping
// one-to-one in both directions.
export const sourceNameMap = (earlierFields) => {
  const used = new Set();
  const byId = new Map();
  // Ordered fields, not a lookup map — numbering repeats depends on the order
  // they appear in. Guarded so a caller passing the wrong shape renders without
  // its names rather than throwing mid-edit.
  (Array.isArray(earlierFields) ? earlierFields : []).forEach((f) => {
    if (!f || !f.id) return;
    // Flattened to one line with no braces: either would terminate the token
    // early (a token never spans a newline), leaving it unconvertible and
    // reaching respondents as literal {{answer: …}} text. Labels can hold an
    // embedded newline — the backend strips only the ends.
    const base =
      pipingSourceName(f).replace(/[{}]/g, "").replace(/\s+/g, " ").trim() ||
      "untitled question";
    // Count up until the name is free, rather than counting occurrences of the
    // base: with labels "Foo", "Foo", "Foo (2)", numbering by occurrence hands
    // "Foo (2)" to the second AND third question, and the reverse mapping then
    // keeps only one of them — the same wrong-answer bug one level down. A
    // generated name has to clear every name already taken, literal ones
    // included.
    let name = base;
    let nth = 1;
    while (used.has(name)) {
      nth += 1;
      name = `${base} (${nth})`;
    }
    used.add(name);
    byId.set(f.id, name);
  });
  return byId;
};

// Stored -> editable. A token whose source is gone stays in its raw {{field:…}}
// form: the author should see that something is broken, and the label editor
// flags it.
export const toDisplayLabel = (text, earlierFields) => {
  if (!text || typeof text !== "string") return text || "";
  const names = sourceNameMap(earlierFields);
  return text.replace(new RegExp(PIPE_RE.source, "g"), (raw, id) =>
    names.has(id) ? `{{answer: ${names.get(id)}}}` : raw
  );
};

// Editable -> stored. Names are matched against the questions that come before
// this one; when several share wording (two "What makes you feel that way?"),
// the nth token of that name takes the nth such question, which is the order
// they were inserted in.
export const fromDisplayLabel = (text, earlierFields) => {
  if (!text || typeof text !== "string") return text || "";
  const idByName = new Map();
  sourceNameMap(earlierFields).forEach((name, id) => idByName.set(name, id));
  return text.replace(ANSWER_RE, (raw, rawName) => {
    const id = idByName.get((rawName || "").trim());
    // Not a question that comes before this one (renamed, moved, or typed by
    // hand) — leave the text exactly as the author wrote it rather than
    // silently binding it to the wrong question or dropping it.
    return id === undefined ? raw : pipingToken(id);
  });
};

// Flatten to a single line. Labels and headings are one line of prose wherever
// they are rendered, and a stored value can still hold an embedded newline (the
// backend strips only the ends).
export const singleLine = (text) =>
  typeof text === "string" ? text.replace(/[\r\n]+/g, " ") : text || "";

// Is this the wording of a question that comes before the one being edited? A
// token naming something else is just text the author happened to type (or a
// token they edited into meaninglessness), and reads as plain text everywhere.
export const isKnownSourceName = (name, earlierFields) => {
  const wanted = (name || "").trim();
  let found = false;
  sourceNameMap(earlierFields).forEach((n) => {
    if (n === wanted) found = true;
  });
  return found;
};

// Split a display string into literal chunks and {{answer: name}} tokens, for
// surfaces that style the token differently from the text around it. Each token
// segment carries the name it holds, so callers can tell a live reference from
// text that merely looks like one.
export const splitAnswerTokens = (text) => {
  const out = [];
  if (typeof text !== "string" || !text) return out;
  const re = new RegExp(ANSWER_RE.source, "g");
  let last = 0;
  let m = re.exec(text);
  while (m !== null) {
    if (m.index > last) out.push({ text: text.slice(last, m.index) });
    out.push({ token: m[0], name: (m[1] || "").trim() });
    last = m.index + m[0].length;
    m = re.exec(text);
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
};

// Ids referenced by piping tokens in a string.
export const pipingRefs = (text) => {
  const out = [];
  if (typeof text !== "string") return out;
  text.replace(PIPE_RE, (_m, id) => {
    out.push(id);
    return _m;
  });
  return out;
};

// Remove every {{field:id}} token for `id` from a string (tidying doubled
// spaces), used when the source field is deleted.
export const stripPipingFor = (text, id) => {
  if (typeof text !== "string" || !text) return text;
  const re = new RegExp(`\\{\\{\\s*field:${id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*\\}\\}`, "g");
  return text.replace(re, "").replace(/\s{2,}/g, " ").trim();
};

// Piping tokens must reference an EARLIER answerable field; returns the ids
// that don't (missing, later, or a heading).
export const danglingPipingRefs = (field, earlierFields) => {
  const ok = new Set(
    (earlierFields || []).filter((f) => !isDisplayOnlyType(f.type)).map((f) => f.id)
  );
  return [...pipingRefs(field.label), ...pipingRefs(field.help)].filter((id) => !ok.has(id));
};
